import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3Icon, EyeIcon, GlobeIcon, LayersIcon, LineChartIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const NARRATIVE = ['Brands grow when they receive attention, relevance and love.', 'Until now, giving more brands more marketing love, more often, has been too costly and too complex to scale.', 'Snakker — and technology — changes that equation.'] as const;
const AGENDA = [{
  label: 'Attention',
  detail: 'Why brands have more potential when they receive relevant attention more often.',
  icon: EyeIcon
}, {
  label: 'Economics',
  detail: 'Why ongoing campaign attention has historically been hard to scale sustainably.',
  icon: BarChart3Icon
}, {
  label: 'Model',
  detail: 'How Snakker turns approved content into reusable, interactive campaign experiences.',
  icon: LayersIcon
}, {
  label: 'Insight',
  detail: 'How every interaction creates learning that improves the next campaign.',
  icon: LineChartIcon
}, {
  label: 'Scale',
  detail: 'Where Snakker can support your brand portfolio across brands, markets and channels.',
  icon: GlobeIcon
}] as const;
export function SlideMeetingPurpose({}: SlideProps) {
  return <InteriorSlide surface data-id="element-416" data-mp="1vx0kgkhwhw1d">
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-[clamp(1.5rem,3vw,3.5rem)] lg:grid-cols-12" data-id="element-417" data-mp="2tn54o61kpxsv">
        <div className="flex min-h-0 flex-col justify-center lg:col-span-6" data-id="element-418" data-mp="2on91ua4m4cew">
          <motion.p initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45
        }} className="mb-3 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-419" data-mp="2jo5vmmcwc05c">
            Purpose of today’s meeting
          </motion.p>

          <motion.h2 initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.12,
          duration: 0.55
        }} className="text-[clamp(1.9rem,3.1vw,3rem)] font-extrabold leading-[1.05] tracking-[-0.04em] text-[var(--fg)]" data-id="element-420" data-mp="146w20rdpbj2s">
            Brands grow when they receive attention, relevance and{' '}
            <span className="text-[var(--brand)]" data-id="element-421" data-mp="10p88msd10es5">love.</span>
          </motion.h2>

          <div className="mt-[clamp(1.25rem,2vw,2rem)] space-y-4 border-l-2 border-[var(--brand)]/30 pl-5" data-id="element-422" data-mp="mymqx6zb6avu">
            {NARRATIVE.slice(1).map((line, index) => <motion.p key={line} initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.26 + index * 0.12,
            duration: 0.5
          }} className={index === NARRATIVE.length - 2 ? 'text-[clamp(1rem,1.45vw,1.35rem)] font-bold leading-snug text-[var(--fg)]' : 'text-[clamp(0.9rem,1.25vw,1.15rem)] leading-relaxed text-[var(--muted)]'} data-id="element-423" data-mp="rr63q1q691zl" data-mpi={index}>
                {line}
              </motion.p>)}
          </div>

          <motion.p initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.55,
          duration: 0.5
        }} className="mt-[clamp(1.25rem,2vw,2rem)] max-w-2xl text-[clamp(0.9rem,1.25vw,1.15rem)] leading-relaxed text-[var(--muted)]" data-id="element-424" data-mp="3gl64j44wt2af">
            Today, we want to explore how{' '}
            <strong className="font-bold text-[var(--fg)]" data-id="element-425" data-mp="3bi6nsig3nxg">
              your brand teams and Snakker
            </strong>{' '}
            could collaborate on a scalable model for creating, running and
            continuously optimizing short interactive HCP campaigns.
          </motion.p>
        </div>

        <motion.section initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.25,
        duration: 0.6
      }} className="flex min-h-0 flex-col justify-center lg:col-span-6" aria-labelledby="todays-flow-heading" data-id="element-426" data-mp="r2xbbrxp2hbv">
          <h3 id="todays-flow-heading" className="mb-[clamp(0.75rem,1.4vw,1.25rem)] flex-shrink-0 text-[clamp(1.1rem,1.7vw,1.5rem)] font-extrabold tracking-[-0.02em] text-[var(--fg)]" data-id="element-427" data-mp="1a5pfggokik4y">
            Today’s flow
          </h3>

          <ol className="flex flex-col gap-[clamp(0.5rem,0.9vw,0.85rem)]" data-id="element-428" data-mp="n6b548k6io4r">
            {AGENDA.map((item, index) => {
            const Icon = item.icon;
            return <motion.li key={item.label} initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4 + index * 0.09,
              duration: 0.45
            }} className="flex items-start gap-4 rounded-2xl border border-[var(--fg)]/8 bg-white p-[clamp(0.75rem,1.1vw,1.1rem)] shadow-sm" data-id="element-429" data-mp="r9puhpu0cguv" data-mpi={index}>
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[var(--brand)]/10" data-id="element-430" data-mp="2cn2fph42t5il">
                    <Icon className="h-5 w-5 text-[var(--brand)]" aria-hidden="true" data-id="element-431" data-mp="3s3njxiz89xqx" />
                  </span>
                  <div className="min-w-0" data-id="element-432" data-mp="3nb492trxdgpt">
                    <p className="flex items-baseline gap-2 text-[clamp(0.95rem,1.25vw,1.15rem)] font-extrabold leading-tight text-[var(--fg)]" data-id="element-433" data-mp="3idcgryndbsl3">
                      <span className="text-[10px] font-bold tracking-[0.2em] text-[var(--muted)]" data-id="element-434" data-mp="3eafrjp3boy86">
                        0{index + 1}
                      </span>
                      {item.label}
                    </p>
                    <p className="mt-1 text-[clamp(0.75rem,1vw,0.95rem)] leading-snug text-[var(--muted)]" data-id="element-435" data-mp="2zfrp6jt7yfws">
                      {item.detail}
                    </p>
                  </div>
                </motion.li>;
          })}
          </ol>
        </motion.section>
      </div>
    </InteriorSlide>;
}